import React from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import type { Plant } from '../../../../types/plant';

interface SeasonalTip {
  id: string;
  season: string;
  tip: string;
  priority: number;
}

interface SeasonalTipsProps {
  plant: Plant;
}

export default function SeasonalTips({ plant }: SeasonalTipsProps) {
  const [tips, setTips] = React.useState<SeasonalTip[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function fetchTips() {
      if (!plant?.id) return;

      try {
        const { data, error } = await supabase
          .from('seasonal_tips')
          .select('*')
          .eq('plant_id', plant.id)
          .order('priority');

        if (error) throw error;
        setTips(data || []);
      } catch (err) {
        console.error('Error fetching seasonal tips:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchTips();
  }, [plant?.id]); // Only re-run when plant.id changes

  if (!plant?.id || loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600">
        Error loading seasonal tips: {error}
      </div>
    );
  }

  if (tips.length === 0) {
    return null;
  }

  return (
    <section className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-semibold mb-6">Seasonal Tips</h2>
      <div className="space-y-4">
        {tips.map((tip) => (
          <div key={tip.id} className="flex gap-3">
            <AlertCircle className="text-primary-500 flex-shrink-0" />
            <div>
              <p className="font-medium capitalize mb-1">{tip.season}</p>
              <p className="text-gray-600">{tip.tip}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}